import { c as _c } from "react/compiler-runtime";
import { Calendar, Mail, MapPin, WarningSolid } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '~/components/Button';
import { Stack } from '~/components/Layout/Stack';
import { MessagePanel } from '~/components/MessagePanel';
import { PartnerLoader } from '~/components/PartnerLoader/PartnerLoader';
import { PromoBadge } from '~/components/PromoBadge';
import { Tag, TagGroup } from '~/components/TagGroup';
import { usePlaygroundActions, usePlaygroundCurrentSkeletonTrip, usePlaygroundUIState } from '~/stores/playgroundStore';
import { trackEvent } from '~/utils';
import { getCookie } from '~/utils/cookies';
import { CURRENT_PROMO, hasAppliedFreeUpgradePromo } from '~/utils/promoHelper';
import { getTripHighlightPlaceNames, joinPlaceNames } from '~/utils/tripTitle';
import { EditTripButton } from '../Buttons/EditTripButton';
import { EmailCaptureForm, type EmailCaptureFormHandle } from '../EmailCaptureForm/EmailCaptureForm';
import { useEmailCaptureGate } from '../EmailCaptureForm/useEmailCaptureGate';
import { PlaygroundTimeline } from '../PlaygroundTimeline';
import { SaveTripButton } from '../SaveTripButton/SaveTripButton';
import { TripImageCarousel } from '../TripImageCarousel';
import styles from './TripDrawer.module.css';
export default function TripDrawer() {
  const $ = _c(104);
  const t = useTranslations();
  const trip = usePlaygroundCurrentSkeletonTrip();
  const {
    tripPanelResult,
    isOverviewSubmitting,
    tripDrawerInitialFocus
  } = usePlaygroundUIState();
  const {
    setTripDrawerInitialFocus
  } = usePlaygroundActions();
  const gate = useEmailCaptureGate();
  const errorRef = useRef(null);
  const emailFormRef = useRef(null);
  const [emailFormMounted, setEmailFormMounted] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = node => {
      emailFormRef.current = node;
      setEmailFormMounted(node !== null);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const setEmailFormRef = t0;
  let t1;
  let t2;
  if ($[1] !== tripPanelResult) {
    t1 = () => {
      if (tripPanelResult && tripPanelResult.status === "failure" && errorRef.current) {
        errorRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest"
        });
      }
    };
    t2 = [tripPanelResult];
    $[1] = tripPanelResult;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== setTripDrawerInitialFocus || $[5] !== tripDrawerInitialFocus) {
    t3 = () => {
      if (tripDrawerInitialFocus !== "email" || !emailFormRef.current) {
        return;
      }
      const handle = emailFormRef.current;
      const enteringAncestor = handle.getElement()?.closest("[data-entering]");
      if (!enteringAncestor) {
        handle.scrollIntoView();
        setTripDrawerInitialFocus(null);
        return;
      }
      const onAnimationEnd = event => {
        if (event.target !== enteringAncestor) {
          return;
        }
        handle.scrollIntoView();
        setTripDrawerInitialFocus(null);
      };
      enteringAncestor.addEventListener("animationend", onAnimationEnd);
      return () => {
        enteringAncestor.removeEventListener("animationend", onAnimationEnd);
      };
    };
    $[4] = setTripDrawerInitialFocus;
    $[5] = tripDrawerInitialFocus;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== emailFormMounted || $[8] !== setTripDrawerInitialFocus || $[9] !== tripDrawerInitialFocus) {
    t4 = [tripDrawerInitialFocus, setTripDrawerInitialFocus, emailFormMounted];
    $[7] = emailFormMounted;
    $[8] = setTripDrawerInitialFocus;
    $[9] = tripDrawerInitialFocus;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  useEffect(t3, t4);
  let t5;
  if ($[11] !== gate.isLoggedIn) {
    t5 = () => {
      const result = emailFormRef.current?.submitFromExternalCta();
      return result === "invalid" && !gate.isLoggedIn ? "block" : "continue";
    };
    $[11] = gate.isLoggedIn;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleBeforeNavigate = t5;
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = fullTripId => {
      emailFormRef.current?.commitEmailCapture(fullTripId);
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleAfterTripBuilt = t6;
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      trackEvent("playground_email_me_click", {
        event_category: "Playground",
        event_label: "playground_email_me_click"
      });
      emailFormRef.current?.scrollIntoView();
    };
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleEmailMePress = t7;
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = getCookie("displayCountry");
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const isUkCustomer = t8 === "GB";
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = CURRENT_PROMO();
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const {
    isPromoLive
  } = t9;
  if (trip) {
    let T0;
    let showPromoBadge;
    let stays;
    let t10;
    let t11;
    let t12;
    let t13;
    let t14;
    let t15;
    let t16;
    if ($[17] !== t || $[18] !== trip.appliedPromotions || $[19] !== trip.items) {
      stays = trip.items.filter(_temp);
      let t17;
      if ($[30] !== trip.appliedPromotions) {
        t17 = isUkCustomer && isPromoLive && hasAppliedFreeUpgradePromo(trip.appliedPromotions);
        $[30] = trip.appliedPromotions;
        $[31] = t17;
      } else {
        t17 = $[31];
      }
      showPromoBadge = t17;
      const places = joinPlaceNames(getTripHighlightPlaceNames(stays));
      t16 = styles.container;
      t15 = styles.content;
      T0 = Stack;
      t14 = "--by-space-400";
      t12 = styles.header;
      t13 = <TripImageCarousel stays={stays} />;
      t10 = styles.title;
      t11 = t("playground.trip_title_journey", {
        places
      });
      $[17] = t;
      $[18] = trip.appliedPromotions;
      $[19] = trip.items;
      $[20] = T0;
      $[21] = showPromoBadge;
      $[22] = stays;
      $[23] = t10;
      $[24] = t11;
      $[25] = t12;
      $[26] = t13;
      $[27] = t14;
      $[28] = t15;
      $[29] = t16;
    } else {
      T0 = $[20];
      showPromoBadge = $[21];
      stays = $[22];
      t10 = $[23];
      t11 = $[24];
      t12 = $[25];
      t13 = $[26];
      t14 = $[27];
      t15 = $[28];
      t16 = $[29];
    }
    let t17;
    if ($[32] !== t10 || $[33] !== t11) {
      t17 = <h2 className={t10}>{t11}</h2>;
      $[32] = t10;
      $[33] = t11;
      $[34] = t17;
    } else {
      t17 = $[34];
    }
    let t18;
    if ($[35] !== gate.showEmailCapture || $[36] !== t) {
      t18 = (gate.showEmailCapture || process.env.NEXT_PUBLIC_FF_ENABLE_SAVE_IN_MAPS === "true") && <div className={styles.actionsRow}>{gate.showEmailCapture && <Button variant="bare" dataTestId="email-me-button" onPress={handleEmailMePress}><span className={styles.emailMeLabel}><Mail decorative={true} size="large" />{t("account.trip_prompt_email")}</span></Button>}{process.env.NEXT_PUBLIC_FF_ENABLE_SAVE_IN_MAPS === "true" && <SaveTripButton />}</div>;
      $[35] = gate.showEmailCapture;
      $[36] = t;
      $[37] = t18;
    } else {
      t18 = $[37];
    }
    let t19;
    if ($[38] !== showPromoBadge) {
      t19 = showPromoBadge && <PromoBadge variant="inline" />;
      $[38] = showPromoBadge;
      $[39] = t19;
    } else {
      t19 = $[39];
    }
    let t20;
    if ($[40] !== tripPanelResult) {
      t20 = tripPanelResult && <div className={tripPanelResult.status === "success" ? styles.successContainer : styles.errorContainer} ref={tripPanelResult.status === "failure" ? errorRef : null}>{tripPanelResult.status === "success" ? <MessagePanel status="success" message={tripPanelResult.userMessage} dismissable={true} onClose={_temp2} /> : <p className={styles.error}><span className={styles.errorIcon}><WarningSolid decorative={true} /></span>{tripPanelResult.userMessage}</p>}</div>;
      $[40] = tripPanelResult;
      $[41] = t20;
    } else {
      t20 = $[41];
    }
    let t21;
    if ($[42] !== t12 || $[43] !== t13 || $[44] !== t17 || $[45] !== t18 || $[46] !== t19 || $[47] !== t20) {
      t21 = <header className={t12}>{t13}{t17}{t18}{t19}{t20}</header>;
      $[42] = t12;
      $[43] = t13;
      $[44] = t17;
      $[45] = t18;
      $[46] = t19;
      $[47] = t20;
      $[48] = t21;
    } else {
      t21 = $[48];
    }
    let t22;
    if ($[49] !== trip) {
      t22 = <PlaygroundTimeline mode="duration" journey={trip} skeletonTripId={trip.uniqueId} />;
      $[49] = trip;
      $[50] = t22;
    } else {
      t22 = $[50];
    }
    let t23;
    if ($[51] !== t) {
      t23 = t("aria.trip_details");
      $[51] = t;
      $[52] = t23;
    } else {
      t23 = $[52];
    }
    let t24;
    if ($[53] !== t) {
      t24 = t("aria.trip_duration");
      $[53] = t;
      $[54] = t24;
    } else {
      t24 = $[54];
    }
    let t25;
    if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = <Calendar decorative={true} />;
      $[55] = t25;
    } else {
      t25 = $[55];
    }
    const t26 = trip.details.tripInDays - 1;
    let t27;
    if ($[56] !== t || $[57] !== t26) {
      t27 = t("content.forms.nights_count", {
        count: t26
      });
      $[56] = t;
      $[57] = t26;
      $[58] = t27;
    } else {
      t27 = $[58];
    }
    let t28;
    if ($[59] !== t24 || $[60] !== t27) {
      t28 = <Tag aria-label={t24} variant="clear" mode="unselectable">{t25}{t27}</Tag>;
      $[59] = t24;
      $[60] = t27;
      $[61] = t28;
    } else {
      t28 = $[61];
    }
    let t29;
    if ($[62] !== t) {
      t29 = t("aria.trip_places");
      $[62] = t;
      $[63] = t29;
    } else {
      t29 = $[63];
    }
    let t30;
    if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = <MapPin decorative={true} />;
      $[64] = t30;
    } else {
      t30 = $[64];
    }
    let t31;
    if ($[65] !== stays.length || $[66] !== t) {
      t31 = t("playground.places_count", {
        count: stays.length
      });
      $[65] = stays.length;
      $[66] = t;
      $[67] = t31;
    } else {
      t31 = $[67];
    }
    let t32;
    if ($[68] !== t29 || $[69] !== t31) {
      t32 = <Tag aria-label={t29} variant="clear" mode="unselectable">{t30}{t31}</Tag>;
      $[68] = t29;
      $[69] = t31;
      $[70] = t32;
    } else {
      t32 = $[70];
    }
    let t33;
    if ($[71] !== t23 || $[72] !== t28 || $[73] !== t32) {
      t33 = <TagGroup aria-label={t23}>{t28}{t32}</TagGroup>;
      $[71] = t23;
      $[72] = t28;
      $[73] = t32;
      $[74] = t33;
    } else {
      t33 = $[74];
    }
    let t34;
    if ($[75] !== gate.showEmailCapture) {
      t34 = gate.showEmailCapture && <EmailCaptureForm ref={setEmailFormRef} />;
      $[75] = gate.showEmailCapture;
      $[76] = t34;
    } else {
      t34 = $[76];
    }
    let t35;
    if ($[77] !== T0 || $[78] !== t14 || $[79] !== t21 || $[80] !== t22 || $[81] !== t33 || $[82] !== t34) {
      t35 = <T0 space={t14}>{t21}{t22}{t33}{t34}</T0>;
      $[77] = T0;
      $[78] = t14;
      $[79] = t21;
      $[80] = t22;
      $[81] = t33;
      $[82] = t34;
      $[83] = t35;
    } else {
      t35 = $[83];
    }
    let t36;
    if ($[84] !== t15 || $[85] !== t35) {
      t36 = <div className={t15}>{t35}</div>;
      $[84] = t15;
      $[85] = t35;
      $[86] = t36;
    } else {
      t36 = $[86];
    }
    let t37;
    if ($[87] !== isOverviewSubmitting) {
      t37 = clsx(styles.footer, {
        [styles.noBorder]: isOverviewSubmitting
      });
      $[87] = isOverviewSubmitting;
      $[88] = t37;
    } else {
      t37 = $[88];
    }
    let t38;
    if ($[89] !== isOverviewSubmitting) {
      t38 = isOverviewSubmitting && <PartnerLoader variant="map-review-panel" interactive={true} />;
      $[89] = isOverviewSubmitting;
      $[90] = t38;
    } else {
      t38 = $[90];
    }
    let t39;
    if ($[91] !== t38) {
      t39 = <div className={styles.loadingBarContainer}>{t38}</div>;
      $[91] = t38;
      $[92] = t39;
    } else {
      t39 = $[92];
    }
    const t40 = gate.showEmailCapture ? handleBeforeNavigate : undefined;
    const t41 = gate.showEmailCapture ? handleAfterTripBuilt : undefined;
    let t42;
    if ($[93] !== t40 || $[94] !== t41) {
      t42 = <EditTripButton onBeforeNavigate={t40} onAfterTripBuilt={t41} />;
      $[93] = t40;
      $[94] = t41;
      $[95] = t42;
    } else {
      t42 = $[95];
    }
    let t43;
    if ($[96] !== t37 || $[97] !== t39 || $[98] !== t42) {
      t43 = <div className={t37}>{t39}{t42}</div>;
      $[96] = t37;
      $[97] = t39;
      $[98] = t42;
      $[99] = t43;
    } else {
      t43 = $[99];
    }
    let t44;
    if ($[100] !== t16 || $[101] !== t36 || $[102] !== t43) {
      t44 = <div className={t16}>{t36}{t43}</div>;
      $[100] = t16;
      $[101] = t36;
      $[102] = t43;
      $[103] = t44;
    } else {
      t44 = $[103];
    }
    return t44;
  }
  return null;
}
function _temp2() {}
function _temp(item) {
  return item.type === "Stay";
}